import { CheckIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { forwardRef } from 'react';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  id: string;
  error?: string;
  isDirty?: boolean;
}

type Ref = HTMLInputElement;

const Input = forwardRef<Ref, Props>(
  ({ error, id, isDirty, label, ...inputProps }, ref) => {
    return (
      <div className="relative group">
        <label
          htmlFor={id}
          className={classNames(
            'absolute -top-2.5 left-6 h-4 inline-block bg-white px-1 text-11 leading-140 font-bold uppercase',
            {
              'text-blue-800': !error,
            },
            { 'text-error': error },
          )}>
          <div className="hidden group-focus-within:block absolute bottom-0 left-0 bg-iceblue-100 w-full h-1.5 z-0"></div>
          <span className="relative z-1">{label}</span>
        </label>
        <input
          id={id}
          ref={ref}
          className={classNames(
            'w-full text-15 leading-140 tracking-3 text-blue-800 border outline-none rounded-xl md:rounded-3xl pl-7 pr-16 py-4',
            {
              'border-blue-400 focus:bg-iceblue-100 focus:border-blue-400 focus:ring-blue-400':
                !error,
            },
            {
              'border-error focus:border-error focus:ring-error': error,
            },
          )}
          {...inputProps}
        />
        <CheckIcon
          className={classNames(
            'absolute top-4 transition-opacity delay-300 right-6 w-6 h-6',
            { 'opacity-0': !isDirty || error },
            { 'opacity-100': isDirty && !error },
          )}
        />
        {error && (
          <div className="text-error text-11 px-7 pt-1 font-medium">
            {error}
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
